// Core components
export { Button, buttonVariants } from './button';
export { Input, inputVariants } from './input';
export { RadioGroup, RadioGroupItem } from './radio-group';
export { Select, selectVariants } from './select';
export { CountrySelect } from './CountrySelect';
export type { CountrySelectProps } from './CountrySelect';
export { Badge } from './badge';
export { RatingBadge } from './RatingBadge';
export { Logo } from './logo';
export { Link } from './link';
export { Label } from './label';
export { Textarea } from './textarea';
// RichTextEditor is not re-exported: import it from '@/components/ui/rich-text-editor' so its
// editor code and stylesheets load only on pages that use it (PP-143).
import './barrel-page-globals.css';
export { GoogleSignInButton } from './google-signin-button';
export { TimeInput } from './TimeInput';
export { LocalDateTime } from './LocalDateTime';
export type { LocalDateTimeProps } from './LocalDateTime';

// Auth form components
export { AuthForm, AuthFormField, AuthFormActions, AuthFormMessage } from './auth-form';
export { OtpInput } from './otp-input';

// Card components
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  SelectableCard,
  cardVariants,
} from './card';

// Page components
export { PageHeader } from './page-header';

// Metric components
export { MetricCard } from './metric-card';

// Tab components
export { Tab, TabGroup } from './tabs';
export type { TabVariant } from './tabs';
export { PageTabs, TabPanel } from './page-tabs';
export type { PageTabItem, PageTabsProps, TabPanelProps } from './page-tabs';

// Step components
export { Steps, StepIndicator, type Step } from './steps';

// Table components
export { Table, TableHeader, TableCell, TableRow } from './table';
export {
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableHeaderCell,
  DataTableSortableHeader,
  type SortDirection,
  type DataTableSortableHeaderProps,
} from './DataTable';
export { SearchInput, type SearchInputProps } from './SearchInput';
export { Chip, ChipRow, type ChipProps } from './Chip';
export {
  FilterBar,
  type FilterBarProps,
  type FilterBarLabels,
  type FilterGroup,
  type SelectFilterGroup,
  type RangeFilterGroup,
  type MultiFilterGroup,
  type RangeValue,
  selectValue,
  rangeValue,
  multiValue,
  type FilterOption,
  type FilterValue,
} from './FilterBar';
export { Avatar, type AvatarProps } from './Avatar';
export { avatarHue, avatarInitials, type AvatarSize } from './avatarCore';
export { PaginationNav, type PaginationNavProps, type PageSizeOption } from './PaginationNav';
export {
  PaginatedDataTable,
  type PaginatedDataTableProps,
  type ColumnDef,
  type FilterDef,
  type FilterDefRange,
  type LoadParams,
} from './PaginatedDataTable';
export {
  ResponsiveTable,
  useIsAtLeast,
  type ResponsiveTableProps,
  type ResponsiveTableColumn,
  type ResponsiveTableAction,
  type ResponsiveTablePrimaryAction,
} from './responsive-table';
export { useHydrated } from './use-hydrated';
export { RangeSlider, type RangeSliderProps } from './RangeSlider';
export { MultiSelect, type MultiSelectProps, type MultiSelectOption } from './MultiSelect';
// Layout components
export { Container, Stack } from './layout';

// Utility components
export { Alert, AlertTitle, AlertDescription } from './alert';
export { FormField } from './form';
export { InlineEditInput, InlineEditDisplay } from './inline-edit-form';

// Navigation components
export { Sidebar, MainContent, PageContainer } from './navigation';
export { Numeric } from './numeric';

// Specialized components
export { MatchResultBadge, ScoreChange, StatsGrid, ContentGrid, ProfileCard } from './specialized';

// File upload component
export { FileUpload, type UploadedFile } from './FileUpload';

// Theme components
export { ColorPicker } from './ColorPicker';

// Sortable components
export { SortableList } from './sortable-list';

// Toast components
export {
  useToast,
  ToastProvider,
  type ToastType,
  type ToastOptions,
  type ToastAction,
} from './toast';
export { useErrorToast } from './useErrorToast';
export { useErrorMessage } from './useErrorMessage';
export { classifyError, serverText, type ErrorKind } from './errorMessage';

// Dialog components
export {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  type DialogSize,
} from './dialog';
export {
  ConfirmDialog,
  ConfirmProvider,
  useConfirm,
  type ConfirmOptions,
  type ConfirmDialogProps,
} from './ConfirmDialog';
export {
  DirtyGuardProvider,
  useDirtyGuard,
  useDirtyEntries,
  useLeaveGuard,
  useInteractionDirty,
  useSingleFlightSave,
  UnsavedChangesBar,
  type DirtyEntry,
} from './dirty-guard';

// State components (names match src/components/CLAUDE.md)
export { default as LoadingState, default as PageLoading } from './LoadingState';
export { default as EmptyState } from './EmptyState';
export type { EmptyStateProps, EmptyStateAction } from './EmptyState';
export { default as ErrorState, default as PageError } from './ErrorState';
export type { ErrorStateProps } from './ErrorState';
export { Skeleton } from './Skeleton';
export { default as NotAdminState } from './NotAdminState';

// Image components
export { default as Image } from './image';
